/**
 * Normalize numeric input so both comma and dot are accepted as decimal separators.
 * - Uses the last dot/comma as the decimal separator.
 * - Strips other separators/spaces, keeps a leading minus if present.
 * - Returns empty string when nothing numeric is left (allows clearing the field).
 */
export const normalizeDecimalString = (value?: string | number | null): string => {
  if (value === undefined || value === null) return '';

  // If it's already a number, just stringify
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return '';
    return value.toString();
  }

  const trimmed = value.replace(/\s+/g, '');
  const sign = trimmed.startsWith('-') ? '-' : '';
  const unsigned = trimmed.replace(/^-/, '');

  const lastComma = unsigned.lastIndexOf(',');
  const lastDot = unsigned.lastIndexOf('.');
  const sepIndex = Math.max(lastComma, lastDot);

  // No decimal separator — keep only digits
  if (sepIndex === -1) {
    const intOnly = unsigned.replace(/[^\d]/g, '');
    return intOnly ? `${sign}${intOnly}` : '';
  }

  const intPart = unsigned.slice(0, sepIndex).replace(/[^\d]/g, '');
  const fracPart = unsigned.slice(sepIndex + 1).replace(/[^\d]/g, '');

  if (!intPart && !fracPart) return '';
  if (!fracPart) return `${sign}${intPart}`;
  return `${sign}${intPart || '0'}.${fracPart}`;
};

/**
 * Convert input to a number (with comma/dot support). Returns undefined if empty or NaN.
 */
export const toNumberOrUndefined = (value?: string | number | null): number | undefined => {
  const normalized = normalizeDecimalString(value);
  if (!normalized) return undefined;
  const num = Number(normalized);
  return Number.isNaN(num) ? undefined : num;
};


